import { Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { CanvasService } from './canvas.service';
import { CanvasInputMode, CanvasInputModeService } from './canvas-input-mode.service';
import { appendIconChildren } from './connector/connector-utils';
import { ElementAttribute } from '@models/editor';

export enum ConnectorType {
    UNDEFINED = 'undefined',
    STRAIGHT = 'straight',
    CORNER = 'corner',
    DIRECTION = 'direction',
    ENDPOINT = 'endpoint',
    JUNCTION = 'junction',
}

const CONNECTOR_MODES = new Set([
    CanvasInputMode.CONNECTOR_STRAIGHT,
    CanvasInputMode.CONNECTOR_CORNER,
    CanvasInputMode.CONNECTOR_DIRECTION,
    CanvasInputMode.CONNECTOR_ENDPOINT,
    CanvasInputMode.CONNECTOR_JUNCTION,
]);

const CONNECTOR_ICON_MAP: Partial<Record<CanvasInputMode, string>> = {
    [CanvasInputMode.CONNECTOR_STRAIGHT]: 'lines-horizontal',
    [CanvasInputMode.CONNECTOR_CORNER]: 'corner-corner-right-down',
    [CanvasInputMode.CONNECTOR_DIRECTION]: 'direction-right',
    [CanvasInputMode.CONNECTOR_ENDPOINT]: 'end-point-left',
    [CanvasInputMode.CONNECTOR_JUNCTION]: 'cross-or-3-way-cross',
};

const CONNECTOR_TYPE_MAP: Partial<Record<CanvasInputMode, ConnectorType>> = {
    [CanvasInputMode.CONNECTOR_STRAIGHT]: ConnectorType.STRAIGHT,
    [CanvasInputMode.CONNECTOR_CORNER]: ConnectorType.CORNER,
    [CanvasInputMode.CONNECTOR_DIRECTION]: ConnectorType.DIRECTION,
    [CanvasInputMode.CONNECTOR_ENDPOINT]: ConnectorType.ENDPOINT,
    [CanvasInputMode.CONNECTOR_JUNCTION]: ConnectorType.JUNCTION,
};

const ICON_SCALE = 2;
const DEFAULT_CONNECTOR_THICKNESS = 6;

@Injectable({ providedIn: 'root' })
export class ConnectorPlacementService {
    private iconCache = new Map<string, SVGElement>();
    private boundMouseDown = this.handleMouseDown.bind(this);

    private BASE_BAR_THICKNESS_LOCAL = 5;
    private CHEVRON_CROSS_SIZE_LOCAL = 14;
    private CHEVRON_TO_BAR_RATIO = this.CHEVRON_CROSS_SIZE_LOCAL / this.BASE_BAR_THICKNESS_LOCAL;
    private BASE_BAR_THICKNESS_PX = this.BASE_BAR_THICKNESS_LOCAL * 2;
    private BASE_STROKE_PX = 1;
    private MIN_STROKE_PX = 0.5;

    constructor(
        private canvasService: CanvasService,
        private inputModeService: CanvasInputModeService,
        private matIconRegistry: MatIconRegistry
    ) {}

    init() {
        Object.values(CONNECTOR_ICON_MAP).forEach(iconName => {
            this.matIconRegistry.getNamedSvgIcon(iconName).subscribe(svgEl => {
                this.iconCache.set(iconName, svgEl);
            });
        });
        document.addEventListener('mousedown', this.boundMouseDown, {
            capture: true,
            passive: false,
        });
    }

    destroy() {
        document.removeEventListener('mousedown', this.boundMouseDown, {
            capture: true,
        } as EventListenerOptions);
    }

    setConnectorSize(
        connectorElement: SVGGElement,
        size: { width: number; height: number },
        scale?: { sx: number; sy: number }
    ) {
        const fill = connectorElement.querySelector<SVGPathElement>(':scope > path:not([data-bbox-ignore])');
        const lines = Array.from(connectorElement.querySelectorAll<SVGLineElement>(':scope > line'));
        const chevrons = connectorElement.querySelectorAll<SVGPathElement>(':scope > [data-bbox-ignore]');
        if (!fill || lines.length < 2) return;

        const connectorType = connectorElement.getAttribute('data-icreate-connector-icon') ?? '';
        const vertical =
            connectorType == 'direction-direction-3' ||
            connectorType == 'direction-down' ||
            connectorType == 'lines-vertical';

        const m = connectorElement.transform.baseVal.consolidate()?.matrix;
        const { sx, sy } = scale ?? { sx: m ? Math.hypot(m.a, m.b) : 1, sy: m ? Math.hypot(m.c, m.d) : 1 };

        const w = size.width / sx;
        const h = size.height / sy;

        const thicknessPx = vertical ? size.width : size.height;
        const strokePx = Math.max((thicknessPx / this.BASE_BAR_THICKNESS_PX) * this.BASE_STROKE_PX, this.MIN_STROKE_PX);
        const strokeLocal = strokePx / (vertical ? sx : sy);

        const { x, y } = fill.getBBox();

        fill.setAttribute('d', `M${x} ${y}H${x + w}V${y + h}H${x}z`);

        const key = vertical ? 'x1' : 'y1';
        const [first, second] = lines.sort(
            (a, b) => parseFloat(a.getAttribute(key) ?? '0') - parseFloat(b.getAttribute(key) ?? '0')
        );

        const setLine = (line: SVGLineElement, x1: number, y1: number, x2: number, y2: number) => {
            line.setAttribute('x1', String(x1));
            line.setAttribute('y1', String(y1));
            line.setAttribute('x2', String(x2));
            line.setAttribute('y2', String(y2));
            line.setAttribute('stroke-width', String(strokeLocal));
        };

        if (vertical) {
            setLine(first, x, y, x, y + h);
            setLine(second, x + w, y, x + w, y + h);
        } else {
            setLine(first, x, y, x + w, y);
            setLine(second, x, y + h, x + w, y + h);
        }

        const cx = x + w / 2;
        const cy = y + h / 2;

        chevrons.forEach(chev => {
            const cb = chev.getBBox();
            let kx: number, ky: number;

            if (vertical) {
                kx = (w * this.CHEVRON_TO_BAR_RATIO) / cb.width;
                ky = kx * (sx / sy);
                if (cb.height * ky > h) {
                    ky = h / cb.height;
                    kx = ky * (sy / sx);
                }
            } else {
                ky = (h * this.CHEVRON_TO_BAR_RATIO) / cb.height;
                kx = ky * (sy / sx);
                if (cb.width * kx > w) {
                    kx = w / cb.width;
                    ky = kx * (sx / sy);
                }
            }

            const ccx = cb.x + cb.width / 2;
            const ccy = cb.y + cb.height / 2;
            chev.setAttribute('transform', `translate(${cx} ${cy}) scale(${kx} ${ky}) translate(${-ccx} ${-ccy})`);
            chev.setAttribute('stroke-width', String(strokePx));
        });
    }

    getGroupBBoxExcludingIgnored(connectorElement: SVGGElement, applyMatrix = true) {
        let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;

        for (const child of connectorElement.children as any) {
            if (child.hasAttribute('data-bbox-ignore')) continue;
            const b = child.getBBox();
            minX = Number(Math.min(minX, b.x).toFixed(2));
            minY = Number(Math.min(minY, b.y).toFixed(2));
            maxX = Number(Math.max(maxX, b.x + b.width).toFixed(2));
            maxY = Number(Math.max(maxY, b.y + b.height).toFixed(2));
        }
        if (minX === Infinity) return null;

        const local = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
        if (!applyMatrix) return local;

        const consolidated = connectorElement.transform.baseVal.consolidate();
        if (!consolidated) return local;

        const m = consolidated.matrix;
        const corners = [
            [local.x, local.y],
            [local.x + local.width, local.y],
            [local.x, local.y + local.height],
            [local.x + local.width, local.y + local.height],
        ].map(([px, py]) => ({
            x: m.a * px + m.c * py + m.e,
            y: m.b * px + m.d * py + m.f,
        }));

        const xs = corners.map(p => p.x);
        const ys = corners.map(p => p.y);
        const tMinX = Math.min(...xs);
        const tMinY = Math.min(...ys);

        return {
            x: Number(tMinX.toFixed(2)),
            y: Number(tMinY.toFixed(2)),
            width: Number((Math.max(...xs) - tMinX).toFixed(2)),
            height: Number((Math.max(...ys) - tMinY).toFixed(2)),
        };
    }

    private handleMouseDown(event: MouseEvent) {
        const mode = this.inputModeService.getMode();
        if (!CONNECTOR_MODES.has(mode)) return;

        const svgRoot = document.getElementById('svgroot');
        if (!svgRoot.contains(event.target as Node)) return;

        event.preventDefault();
        event.stopPropagation();

        const pt = this.canvasService.toCanvasCoordinates(event.clientX, event.clientY);
        const iconName = CONNECTOR_ICON_MAP[mode];
        const connectorType = CONNECTOR_TYPE_MAP[mode];

        this.inputModeService.setMode(CanvasInputMode.SELECT);
        this.matIconRegistry.getNamedSvgIcon(iconName).subscribe(svgEl => {
            this.placeIcon(svgEl, pt.x, pt.y, connectorType, iconName);
        });
    }

    private placeIcon(svgEl: SVGElement, cx: number, cy: number, connectorType?: string, iconName?: string) {
        const canvas = this.canvasService.getCanvas();
        const svgRoot = document.getElementById('svgroot');
        const layer = svgRoot?.querySelector('.layer');

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('id', canvas.getNextId());
        g.setAttribute(ElementAttribute.resizable, 'true');
        if (connectorType) {
            g.setAttribute(ElementAttribute.connectorType, connectorType);
        }
        if (iconName) {
            g.setAttribute(ElementAttribute.connectorIcon, iconName);
        }
        const svgRootDefs =
            svgRoot.querySelector('defs') ??
            svgRoot.insertBefore(document.createElementNS('http://www.w3.org/2000/svg', 'defs'), svgRoot.firstChild);
        appendIconChildren(svgEl, g, () => canvas.getNextId(), svgRootDefs);

        layer.appendChild(g);
        g.setAttribute('transform', `matrix(${ICON_SCALE}, 0, 0, ${ICON_SCALE}, 0, 0)`);
        this.applyDefaultThickness(g, connectorType);

        const bbox = (g as any).getBBox();
        const bboxCx = bbox.x + bbox.width / 2;
        const bboxCy = bbox.y + bbox.height / 2;

        const dx = cx - ICON_SCALE * bboxCx;
        const dy = cy - ICON_SCALE * bboxCy;
        g.setAttribute('transform', `matrix(${ICON_SCALE}, 0, 0, ${ICON_SCALE}, ${dx}, ${dy})`);

        canvas.addCommandToHistory(new canvas.history.InsertElementCommand(g));
        canvas.call('changed', [g]);
        canvas.selectOnly([g], false);
    }

    private applyDefaultThickness(groupEl: SVGGElement, connectorType?: string) {
        switch (connectorType) {
            case ConnectorType.STRAIGHT:
            case ConnectorType.DIRECTION: {
                const { width } = this.getGroupBBoxExcludingIgnored(groupEl);
                return this.setConnectorSize(groupEl, { width, height: DEFAULT_CONNECTOR_THICKNESS });
            }
            case ConnectorType.CORNER:
                return this.setCornerThickness(groupEl, DEFAULT_CONNECTOR_THICKNESS);
            case ConnectorType.ENDPOINT:
                return this.setEndpointThickness(groupEl, DEFAULT_CONNECTOR_THICKNESS);
        }
    }

    getStraightThickness(groupEl) {
        const path = groupEl.querySelector('path');
        const { sx, sy } = this.getGroupScale(groupEl);
        const bb = path.getBBox();
        return bb.height <= bb.width ? bb.height * sy : bb.width * sx;
    }

    getGroupScale(groupEl) {
        const m = /matrix\(([^)]+)\)/.exec(groupEl.getAttribute('transform') || '');
        if (!m) return { sx: 1, sy: 1 };
        const [a, b, c, d] = m[1]
            .trim()
            .split(/[\s,]+/)
            .map(Number);
        return { sx: Math.hypot(a, b), sy: Math.hypot(c, d) };
    }

    setConnectorThickness(groupEl, canvasThickness, scale = this.getGroupScale(groupEl)) {
        switch (groupEl.getAttribute('data-icreate-connector-type')) {
            case ConnectorType.CORNER:
                return this.setCornerThickness(groupEl, canvasThickness, scale);
            case ConnectorType.ENDPOINT:
                return this.setEndpointThickness(groupEl, canvasThickness, scale);
        }
    }

    getConnectorThickness(groupEl) {
        switch (groupEl.getAttribute('data-icreate-connector-type')) {
            case ConnectorType.CORNER:
                return this.getCornerThickness(groupEl);
            case ConnectorType.ENDPOINT:
                return this.getEndpointThickness(groupEl);
        }
        return null;
    }

    setCornerThickness(groupEl, canvasThickness, scale = this.getGroupScale(groupEl)) {
        const pathEl = groupEl.querySelector('path');
        if (!pathEl) return;

        const { pts, arcs } = this.parsePath(pathEl.getAttribute('d'));
        if (pts.length < 8 || arcs.length < 2) return;

        const { sx, sy } = scale;

        const pairs = [
            [0, 7],
            [1, 6],
            [2, 5],
            [3, 4],
        ];
        const np = new Array(8);

        for (const [i, o] of pairs) {
            const a = pts[i],
                b = pts[o];
            const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
            const ux = (b.x - a.x) / len,
                uy = (b.y - a.y) / len;
            const t = canvasThickness / Math.hypot(ux * sx, uy * sy);

            np[i] = { x: a.x, y: a.y };
            np[o] = { x: a.x + ux * t, y: a.y + uy * t };
        }

        const radii = (p, q) => ({ rx: Math.abs(q.x - p.x), ry: Math.abs(q.y - p.y) });
        const ai = radii(np[1], np[2]);
        const ao = radii(np[5], np[6]);

        const f = n => +n.toFixed(2);
        const P = p => `${f(p.x)},${f(p.y)}`;

        const d = [
            `M${P(np[0])}`,
            `L${P(np[1])}`,
            `A${f(ai.rx)},${f(ai.ry)} 0 ${arcs[0].large} ${arcs[0].sweep} ${P(np[2])}`,
            `L${P(np[3])}`,
            `L${P(np[4])}`,
            `L${P(np[5])}`,
            `A${f(ao.rx)},${f(ao.ry)} 0 ${arcs[1].large} ${arcs[1].sweep} ${P(np[6])}`,
            `L${P(np[7])}`,
            'z',
        ].join(' ');

        pathEl.setAttribute('d', d);
    }

    getCornerThickness(groupEl) {
        const pathEl = groupEl.querySelector('path');
        if (!pathEl) return null;

        const { pts, arcs } = this.parsePath(pathEl.getAttribute('d'));
        if (pts.length < 8 || arcs.length < 2) return null;

        const { sx, sy } = this.getGroupScale(groupEl);

        const pairs = [
            [0, 7],
            [1, 6],
            [2, 5],
            [3, 4],
        ];

        const thicknesses = pairs.map(([i, o]) => {
            const dx = pts[o].x - pts[i].x;
            const dy = pts[o].y - pts[i].y;
            const len = Math.hypot(dx, dy) || 1;
            return len * Math.hypot((dx / len) * sx, (dy / len) * sy);
        });

        return thicknesses.reduce((a, b) => a + b, 0) / thicknesses.length;
    }

    setEndpointThickness(groupEl, canvasThickness, scale = this.getGroupScale(groupEl)) {
        const pathEl = groupEl.querySelector('path');
        if (!pathEl) return;

        const parsed = this.parsePath(pathEl.getAttribute('d'));
        if (parsed.pts.length < 16 || parsed.arcs.length < 8) return;

        const { pts, arcs, sx, sy, fromFrame } = this.toEndpointFrame(parsed, scale);

        const cx = (pts[3].x + pts[15].x) / 2;
        const top = pts[0].y;
        const barLeft = pts[11].x;
        const barRight = pts[7].x;
        const barTop = pts[4].y;

        const tStem = canvasThickness / Math.abs(sx);
        const tBar = canvasThickness / Math.abs(sy);

        const sL = cx - tStem / 2;
        const sR = cx + tStem / 2;

        const bT = barTop;
        const bB = barTop + tBar;

        const r = Math.min(arcs[0].rx, tStem / 2, tBar / 2);
        const rf = arcs[1].rx;

        const f = n => +n.toFixed(2);
        const pt = (x, y) => {
            const p = fromFrame({ x, y });
            return `${f(p.x)},${f(p.y)}`;
        };
        const arc = (rad, a, x, y) => `A${f(rad)},${f(rad)} 0 ${a.large} ${a.sweep} ${pt(x, y)}`;

        const d = [
            `M${pt(sL + r, top)}`,
            `L${pt(sR - r, top)}`,
            arc(r, arcs[0], sR, top + r),
            `L${pt(sR, bT - rf)}`,
            arc(rf, arcs[1], sR + rf, bT),
            `L${pt(barRight - r, bT)}`,
            arc(r, arcs[2], barRight, bT + r),
            `L${pt(barRight, bB - r)}`,
            arc(r, arcs[3], barRight - r, bB),
            `L${pt(barLeft + r, bB)}`,
            arc(r, arcs[4], barLeft, bB - r),
            `L${pt(barLeft, bT + r)}`,
            arc(r, arcs[5], barLeft + r, bT),
            `L${pt(sL - rf, bT)}`,
            arc(rf, arcs[6], sL, bT - rf),
            `L${pt(sL, top + r)}`,
            arc(r, arcs[7], sL + r, top),
            'z',
        ].join(' ');

        pathEl.setAttribute('d', d);
    }

    getEndpointThickness(groupEl) {
        const pathEl = groupEl.querySelector('path');
        if (!pathEl) return null;

        const parsed = this.parsePath(pathEl.getAttribute('d'));
        if (parsed.pts.length < 16 || parsed.arcs.length < 8) return null;

        const { pts, sx, sy } = this.toEndpointFrame(parsed, this.getGroupScale(groupEl));

        const stem = (pts[3].x - pts[15].x) * Math.abs(sx);
        const bar = (pts[8].y - pts[4].y) * Math.abs(sy);

        return (stem + bar) / 2;
    }

    toEndpointFrame({ pts, arcs }, { sx, sy }) {
        const unit = (a, b) => {
            const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
            return { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
        };
        const u = unit(pts[15], pts[2]);
        const v = unit(pts[2], pts[3]);
        const toFrame = p => ({ x: p.x * u.x + p.y * u.y, y: p.x * v.x + p.y * v.y });
        const fromFrame = p => ({ x: p.x * u.x + p.y * v.x, y: p.x * u.y + p.y * v.y });
        return {
            pts: pts.map(toFrame),
            arcs,
            sx: Math.hypot(u.x * sx, u.y * sy),
            sy: Math.hypot(v.x * sx, v.y * sy),
            fromFrame,
        };
    }

    parsePath(d) {
        const tokens = d.match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+\.?)(?:e[-+]?\d+)?/g);
        const pts = [],
            arcs = [];
        let i = 0,
            cmd = '',
            x = 0,
            y = 0;
        const num = () => parseFloat(tokens[i++]);

        while (i < tokens.length) {
            if (/[a-zA-Z]/.test(tokens[i])) cmd = tokens[i++];
            const rel = cmd === cmd.toLowerCase();
            const C = cmd.toUpperCase();

            if (C === 'Z') continue;
            if (C === 'M' || C === 'L') {
                const nx = num(),
                    ny = num();
                x = rel ? x + nx : nx;
                y = rel ? y + ny : ny;
            } else if (C === 'H') {
                const nx = num();
                x = rel ? x + nx : nx;
            } else if (C === 'V') {
                const ny = num();
                y = rel ? y + ny : ny;
            } else if (C === 'A') {
                const rx = num(),
                    ry = num(),
                    rot = num();
                const large = num(),
                    sweep = num();
                const nx = num(),
                    ny = num();
                x = rel ? x + nx : nx;
                y = rel ? y + ny : ny;
                arcs.push({ rx, ry, rot, large, sweep });
            } else {
                return { pts: [], arcs: [] };
            }
            pts.push({ x, y });
        }
        const first = pts[0],
            last = pts[pts.length - 1];
        if (pts.length > 1 && Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6) pts.pop();
        return { pts, arcs };
    }
}
